import React, {useEffect, useRef, useState} from 'react';
import {Form, Input} from 'antd';
import {CheckCircleOutlined, CloseCircleOutlined} from '@ant-design/icons';
import i18n, {t} from 'i18n';
import {BolDataV3, BolStop, TaskGroupType} from './types/bolTypes';
import {useMutation} from '@apollo/client';
import {UPDATE_TMS_TASK_BOL_INFO, BolInfoUpdateInput} from '../../../../../../graphql/mutations/tms/updateTmsTaskBolInfo';
import {useWarehouseContext} from '../../../../../../contexts/WarehouseContext';
import {getAbbrSortCode} from './ts/bolUtils';
import {
    SaveStatus,
    SHIP_TO_FIELD_BASES,
    ShipToFieldBase,
    SHIP_TO_REQUIRED_FIELDS,
    CARRIER_FIELD_BASES,
    CarrierFieldBase,
    CARRIER_REQUIRED_FIELDS,
    CARRIER_FIELD_LABEL_KEYS,
    CARRIER_FIELD_PLACEHOLDER_KEYS,
    buildShipToFieldMeta,
    buildStopFormFields,
    buildSealFieldName,
    getSealSegments
} from './ts/bolEditorShared';

interface BolHandoverFormEditorProps {
    bolData: BolDataV3;
    onDataChange: (data: BolDataV3) => void;
    taskGroupType?: TaskGroupType;
}

/**
 * 交接类型 BOL 编辑器（交接路线任务 / 三方任务如 TikTok）
 * 与非交接编辑器（BolFormEditor）的区别：
 * - 地址信息只编辑始发站（SHIP FROM），无终到站/途经站模块
 * - 承运商信息标题为「交接方信息」
 */
const BolHandoverFormEditor: React.FC<BolHandoverFormEditorProps> = ({
    bolData,
    onDataChange,
    taskGroupType
}) => {
    const [form] = Form.useForm();
    const editorScrollRef = useRef<HTMLDivElement>(null);
    const fieldOriginalValuesRef = useRef<Record<string, any>>({});
    const initialBolDataRef = useRef<BolDataV3 | null>(null);
    const [updateBolInfo] = useMutation(UPDATE_TMS_TASK_BOL_INFO);
    const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);
    const saveStatusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // 获取当前登录仓库信息
    const { currentWarehouse } = useWarehouseContext();

    // 初始化时保存深拷贝的初始数据
    initialBolDataRef.current ??= structuredClone(bolData);

    /** 显示保存状态提示，1 秒后自动消失 */
    const showSaveStatus = (status: SaveStatus) => {
        setSaveStatus(status);
        if (saveStatusTimeoutRef.current) {
            clearTimeout(saveStatusTimeoutRef.current);
        }
        saveStatusTimeoutRef.current = setTimeout(() => {
            setSaveStatus(null);
        }, 1000);
    };

    // 根据当前语言决定是否在字段名和"必填"之间加空格
    const getRequiredMessage = (fieldKey: string): string => {
        const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
        const fieldName = String(t(fieldKey));
        const requiredText = String(t('components.input.required'));
        const space = currentLang === 'zh' ? '' : ' ';
        return fieldName + space + requiredText;
    };

    // 用 warehouse id 比较，不使用sortingCode比较，避免 LA/LAX 等 sortCode 命名差异
    const warehouseIdMatches = (stopId: string) =>
        currentWarehouse?.id != null && String(stopId) === String(currentWarehouse.id);

    // 始发站地址是否可编辑：仅本站发出（当前登录仓库即始发站）时可编辑
    const canEditShipFrom = (): boolean => {
        if (currentWarehouse?.id == null || bolData.stops.length === 0) return false;
        return warehouseIdMatches(bolData.stops[0].shipToId);
    };

    const shipFromStop: BolStop | undefined = bolData.stops[0];
    const sealSegments = getSealSegments(bolData.stops, bolData.taskType);

    /** 更新指定 stop：调用方传更新函数，写真实属性 */
    const updateStop = (stopIndex: number, update: (stop: BolStop) => BolStop): BolDataV3 => ({
        ...bolData,
        stops: bolData.stops.map((stop, idx) => (idx === stopIndex ? update(stop) : stop))
    });

    /**
     * 保存到数据库：input 只包含本次变更的字段，后端只更新实际传入的字段
     * @param input 变更字段补丁（写真实属性，无字段名字符串）
     * @param stopIndex 站点级字段所在 stop 下标；任务级字段（交接方信息）传 null
     */
    const saveToDatabase = async (input: BolInfoUpdateInput, stopIndex: number | null = null) => {
        if (!bolData.taskId) {
            console.warn('Missing taskId for saving BOL info');
            return;
        }

        const variables: Record<string, unknown> = {
            taskId: Number.parseInt(bolData.taskId),
            input
        };
        if (stopIndex !== null) {
            variables.segment = bolData.stops[stopIndex].segment;
        }

        try {
            const { data } = await updateBolInfo({ variables });
            // 成功与否直接取自返回值：后端更新失败会抛异常走 catch，返回空同样视为失败
            const ok = Boolean(data?.updateTmsTaskBolInfo);
            if (ok) {
                // 保存成功：把补丁合并进本地预览数据（null 统一落为 ''）
                const patch: Partial<Record<keyof BolInfoUpdateInput, string>> = {};
                (Object.keys(input) as Array<keyof BolInfoUpdateInput>).forEach((key) => {
                    patch[key] = input[key] ?? '';
                });
                const updatedData = stopIndex === null
                    ? { ...bolData, carrier: { ...bolData.carrier, ...patch } }
                    : updateStop(stopIndex, stop => ({ ...stop, ...patch }));
                onDataChange(updatedData);
                initialBolDataRef.current = structuredClone(updatedData);
            } else {
                // 保存失败：预览还原为初始值（表单值由 useEffect 按 bolData 回写）
                onDataChange(structuredClone(initialBolDataRef.current || bolData));
            }
            showSaveStatus(ok ? SaveStatus.Success : SaveStatus.Error);
        } catch (error) {
            // 保存异常：预览还原为初始值（表单值由 useEffect 按 bolData 回写）
            onDataChange(structuredClone(initialBolDataRef.current || bolData));
            showSaveStatus(SaveStatus.Error);
        }
    };

    /**
     * 交接方信息：输入时实时更新 BOL 预览
     * @param fieldName
     */
    const handleCarrierInputChange = (fieldName: string) => () => {
        const fieldValue = form.getFieldValue(fieldName);
        // 保存原始值（第一次 onChange 时）
        if (!(fieldName in fieldOriginalValuesRef.current)) {
            fieldOriginalValuesRef.current[fieldName] = bolData.carrier[fieldName as keyof typeof bolData.carrier];
        }
        const updatedData = structuredClone(bolData);
        updatedData.carrier[fieldName as keyof typeof updatedData.carrier] = fieldValue;
        onDataChange(updatedData);
    };

    // 交接方信息字段失焦 - 保存到数据库
    const handleCarrierInputBlur = (fieldName: string) => () => {
        const fieldValue = form.getFieldValue(fieldName);
        if (!(fieldName in fieldOriginalValuesRef.current)) {
            return;
        }

        const originalValue = fieldOriginalValuesRef.current[fieldName];

        // 如果是必填项且被清空了，不保存
        if (CARRIER_REQUIRED_FIELDS.includes(fieldName as CarrierFieldBase) && !fieldValue) {
            return;
        }

        // 只有当值改变时才执行保存
        if (fieldValue !== originalValue) {
            saveToDatabase({ [fieldName]: fieldValue } as BolInfoUpdateInput);
        }

        // 清除该字段的原始值记录
        delete fieldOriginalValuesRef.current[fieldName];
    };

    /**
     * 封签号可编辑逻辑：本站发出可编辑「从本站出发的段」及之后所有段；到达本站为终点时无权限
     */
    const canEditSealField = (stopIndex: number): boolean => {
        if (!currentWarehouse?.id) return false;
        const stop = bolData.stops[stopIndex];
        if (!stop) return false;

        const lastStop = bolData.stops[bolData.stops.length - 1];
        if (!lastStop) return false;

        if (taskGroupType === TaskGroupType.Arrival && warehouseIdMatches(lastStop.shipToId)) {
            return false;
        }

        const firstSegmentFromCurrentStation = bolData.stops.findIndex(s => warehouseIdMatches(s.shipFromId));
        if (firstSegmentFromCurrentStation === -1) return false;
        return stopIndex >= firstSegmentFromCurrentStation;
    };

    /**
     * 封签号：输入时实时更新 BOL 预览
     */
    const handleSealNumberInputChange = (stopIndex: number) => {
        const fieldName = buildSealFieldName(stopIndex);
        const fieldValue = form.getFieldValue(fieldName);

        if (!(fieldName in fieldOriginalValuesRef.current)) {
            fieldOriginalValuesRef.current[fieldName] = bolData.stops[stopIndex]?.sealNumber ?? '';
        }

        if (!canEditSealField(stopIndex)) {
            return;
        }

        const normalizedValue = typeof fieldValue === 'string' ? fieldValue : String(fieldValue ?? '');
        onDataChange(updateStop(stopIndex, stop => ({ ...stop, sealNumber: normalizedValue })));
    };

    /**
     * 封签号：失焦时保存到数据库
     */
    const handleSealNumberInputBlur = (stopIndex: number) => {
        const fieldName = buildSealFieldName(stopIndex);
        const fieldValue = form.getFieldValue(fieldName);
        const originalValue = fieldOriginalValuesRef.current[fieldName] ?? bolData.stops[stopIndex]?.sealNumber ?? '';

        if (fieldValue !== originalValue) {
            if (!canEditSealField(stopIndex)) {
                form.setFieldValue(fieldName, originalValue);
                setTimeout(() => form.setFieldValue(fieldName, originalValue), 0);
                onDataChange(structuredClone(initialBolDataRef.current || bolData));
                delete fieldOriginalValuesRef.current[fieldName];
                return;
            }
            const valueToSave = (fieldValue == null || String(fieldValue).trim() === '') ? null : fieldValue;
            saveToDatabase({ sealNumber: valueToSave }, stopIndex);
        }

        delete fieldOriginalValuesRef.current[fieldName];
    };

    /**
     * 始发站信息：输入时实时更新 BOL 预览
     */
    const handleStopInputChange = (fieldBase: ShipToFieldBase) => {
        const { formName, fieldKey } = buildShipToFieldMeta(fieldBase, 0);
        if (!shipFromStop || !canEditShipFrom()) return;

        const fieldValue = form.getFieldValue(formName);

        if (!(formName in fieldOriginalValuesRef.current)) {
            fieldOriginalValuesRef.current[formName] = shipFromStop[fieldKey];
        }

        const normalizedValue = typeof fieldValue === 'string' ? fieldValue : String(fieldValue ?? '');
        onDataChange(updateStop(0, stop => ({ ...stop, [fieldKey]: normalizedValue } as BolStop)));
    };

    /**
     * 始发站信息：失焦时保存到数据库
     */
    const handleStopInputBlur = (fieldBase: ShipToFieldBase) => {
        const { formName } = buildShipToFieldMeta(fieldBase, 0);
        const fieldValue = form.getFieldValue(formName);
        const originalValue = fieldOriginalValuesRef.current[formName];

        if (SHIP_TO_REQUIRED_FIELDS.includes(fieldBase) && !fieldValue) {
            return;
        }

        if (fieldValue !== originalValue) {
            // 无权限时：还原表单值、还原预览数据并返回，不发起保存请求
            if (!shipFromStop || !canEditShipFrom()) {
                const { fieldKey } = buildShipToFieldMeta(fieldBase, 0);
                form.setFieldValue(formName, originalValue ?? shipFromStop?.[fieldKey] ?? '');
                onDataChange(structuredClone(initialBolDataRef.current || bolData));
                delete fieldOriginalValuesRef.current[formName];
                return;
            }

            // stops[0]（始发站）在库里没有独立段行：其信息即第 1 段的 shipFrom，直接写第 1 段
            if (bolData.stops.length > 1) {
                const nextField = fieldBase.replace('shipTo', 'shipFrom');
                saveToDatabase({ [nextField]: fieldValue } as BolInfoUpdateInput, 1);
            }
        }

        delete fieldOriginalValuesRef.current[formName];
    };

    useEffect(() => {
        const formValues: Record<string, any> = {};
        CARRIER_FIELD_BASES.forEach((fb) => {
            formValues[fb] = bolData.carrier[fb as keyof typeof bolData.carrier];
        });

        if (shipFromStop) {
            const fields = buildStopFormFields(0);
            formValues[fields.address.formName] = shipFromStop.shipToAddress;
            formValues[fields.contactName.formName] = shipFromStop.shipToContactName;
            formValues[fields.contactPhone.formName] = shipFromStop.shipToContactPhoneNumber;
        }

        sealSegments.forEach((segment) => {
            formValues[buildSealFieldName(segment.stopIndex)] = segment.sealNumber;
        });

        form.setFieldsValue(formValues);
    }, [bolData, form, shipFromStop, sealSegments]);

    // 清理 timeout
    useEffect(() => {
        return () => {
            if (saveStatusTimeoutRef.current) {
                clearTimeout(saveStatusTimeoutRef.current);
            }
        };
    }, []);

    const canEdit = canEditShipFrom();
    const fields = buildStopFormFields(0);

    return (
        <div className="flex-1 border border-gray-300 rounded bg-white flex flex-col overflow-hidden min-w-[420px]">
            <h3 className="px-5 pt-3 pb-2 bg-white border-b border-gray-200 flex-shrink-0 whitespace-nowrap rounded-t flex items-center justify-between">
                <span>{String(t('tms.task.bol.edit'))}</span>
                {saveStatus === SaveStatus.Success && (
                    <span className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircleOutlined />
                        {String(t('common.messages.saved'))}
                    </span>
                )}
                {saveStatus === SaveStatus.Error && (
                    <span className="flex items-center gap-1 text-red-600 text-sm">
                        <CloseCircleOutlined />
                        {String(t('tms.task.bol.errors.failedToSave'))}
                    </span>
                )}
            </h3>
            <div ref={editorScrollRef} className="overflow-y-auto flex-1 rounded-b">
                <div className="p-5">
                    <Form
                        form={form}
                        layout="vertical"
                    >
                        <div className="flex flex-col gap-4">
                            {shipFromStop && (
                                <div className="border-b pb-4 bol-address-grid">
                                    <h4 className="mb-3 font-semibold">{String(t('tms.task.bol.addressInformation'))}</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2 font-semibold text-sm">{String(t('tms.task.bol.stopLabels.pickUp'))}({getAbbrSortCode(shipFromStop.shipToSortCode)})</div>
                                        <Form.Item
                                            label={String(t('tms.task.bol.address'))}
                                            name={fields.address.formName}
                                            initialValue={shipFromStop.shipToAddress}
                                            style={{ marginBottom: 5 }}
                                            rules={[{ required: true, message: getRequiredMessage('tms.task.bol.address') }]}
                                        >
                                            <Input
                                                placeholder={String(t('tms.task.bol.placeholderAddress'))}
                                                allowClear
                                                disabled={!canEdit}
                                                onChange={() => handleStopInputChange(SHIP_TO_FIELD_BASES[0])}
                                                onBlur={() => handleStopInputBlur(SHIP_TO_FIELD_BASES[0])}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            label={String(t('tms.task.bol.cityStateZip'))}
                                            name={fields.cityStateZip.formName}
                                            initialValue={shipFromStop.shipToCityStateZipcode}
                                            style={{ marginBottom: 5 }}
                                            rules={[{ required: true, message: getRequiredMessage('tms.task.bol.cityStateZip') }]}
                                        >
                                            <Input
                                                placeholder={String(t('tms.task.bol.placeholderCityStateZip'))}
                                                allowClear
                                                disabled={!canEdit}
                                                onChange={() => handleStopInputChange(SHIP_TO_FIELD_BASES[1])}
                                                onBlur={() => handleStopInputBlur(SHIP_TO_FIELD_BASES[1])}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            label={String(t('tms.task.bol.contactName'))}
                                            name={fields.contactName.formName}
                                            initialValue={shipFromStop.shipToContactName}
                                            style={{ marginBottom: 5 }}
                                            rules={[{ required: true, message: getRequiredMessage('tms.task.bol.contactName') }]}
                                        >
                                            <Input
                                                placeholder={String(t('tms.task.bol.placeholderContactName'))}
                                                allowClear
                                                disabled={!canEdit}
                                                onChange={() => handleStopInputChange(SHIP_TO_FIELD_BASES[2])}
                                                onBlur={() => handleStopInputBlur(SHIP_TO_FIELD_BASES[2])}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            label={String(t('tms.task.bol.contactNumber'))}
                                            name={fields.contactPhone.formName}
                                            initialValue={shipFromStop.shipToContactPhoneNumber}
                                            style={{ marginBottom: 5 }}
                                            rules={[{ required: true, message: getRequiredMessage('tms.task.bol.contactNumber') }]}
                                        >
                                            <Input
                                                placeholder={String(t('tms.task.bol.placeholderContactNumber'))}
                                                allowClear
                                                disabled={!canEdit}
                                                onChange={() => handleStopInputChange(SHIP_TO_FIELD_BASES[3])}
                                                onBlur={() => handleStopInputBlur(SHIP_TO_FIELD_BASES[3])}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>
                            )}

                            <div className="border-b pb-4">
                                <h4 className="mb-3 font-semibold">{String(t('tms.task.bol.handoverCarrierInformation'))}</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    {CARRIER_FIELD_BASES.map((fb) => {
                                        const labelKey = CARRIER_FIELD_LABEL_KEYS[fb];
                                        const placeholderKey = CARRIER_FIELD_PLACEHOLDER_KEYS[fb];
                                        const label = labelKey.startsWith('tms.') ? String(t(labelKey)) : labelKey;
                                        const placeholder = placeholderKey.startsWith('tms.') ? String(t(placeholderKey)) : placeholderKey;
                                        const isRequired = CARRIER_REQUIRED_FIELDS.includes(fb);
                                        return (
                                            <Form.Item
                                                key={fb}
                                                label={label}
                                                name={fb}
                                                rules={isRequired ? [{ required: true, message: getRequiredMessage(labelKey) }] : undefined}
                                                style={{ marginBottom: 5 }}
                                            >
                                                <Input
                                                    placeholder={placeholder}
                                                    allowClear
                                                    onChange={() => handleCarrierInputChange(fb)()}
                                                    onBlur={() => handleCarrierInputBlur(fb)()}
                                                />
                                            </Form.Item>
                                        );
                                    })}
                                </div>
                            </div>

                            {sealSegments.length > 0 && (
                                <div className="border-b pb-4">
                                    <h4 className="mb-3 font-semibold">{String(t('tms.task.bol.sealNumberInformation'))}</h4>
                                    <div className="flex flex-col gap-3 ml-4">
                                        {sealSegments.map((segment, idx) => {
                                            const isSealEditable = canEditSealField(segment.stopIndex);
                                            return (
                                                <div key={segment.stopIndex} className="flex items-center gap-2">
                                                    <div className="flex items-center gap-1 w-[100px] text-sm">
                                                        <span className="flex items-center justify-center">
                                                            {idx + 1}.
                                                        </span>
                                                        <span className="whitespace-normal leading-tight">{segment.label} :</span>
                                                    </div>
                                                    <Form.Item
                                                        name={buildSealFieldName(segment.stopIndex)}
                                                        style={{ marginBottom: 0, flex: 1 }}
                                                        normalize={(v: string) => (v || '').replace(/[^A-Za-z0-9]/g, '')}
                                                    >
                                                        <Input
                                                            placeholder={String(t('tms.task.bol.placeholderSealNumber'))}
                                                            allowClear
                                                            disabled={!isSealEditable}
                                                            onChange={() => handleSealNumberInputChange(segment.stopIndex)}
                                                            onBlur={() => handleSealNumberInputBlur(segment.stopIndex)}
                                                        />
                                                    </Form.Item>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default BolHandoverFormEditor;
